import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import { setTrackContext } from "../utils/track";
import { Link } from "react-router-dom";
import Chart from "react-apexcharts";
import { aggregateHistoryByDay, getTodayTotals } from "../utils/usageUtils";
import {
  FiUpload,
  FiList,
  FiTrendingUp,
  FiBarChart2,
  FiCreditCard,
  FiServer,
  FiZap,
  FiFileText,
  FiFilter,
  FiChevronDown,
} from "react-icons/fi";
import { motion } from "framer-motion";
import { extractInternationalNumber } from "../utils/phoneUtils";
import LoadingSkeleton from "../components/LoadingSkeleton";

// --- Components ---

const StatCard = ({ icon: Icon, label, value, colorClass, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay }}
    className="bg-white/80 backdrop-blur-xl border border-white/60 shadow-lg rounded-2xl p-5 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 group"
  >
    <div className="flex items-center gap-4">
      <div
        className={`p-3 rounded-xl ${colorClass} group-hover:scale-110 transition-transform duration-300 shadow-sm`}
      >
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <p className="text-sm text-gray-500 font-medium mb-1">{label}</p>
        <h4 className="text-2xl font-bold text-gray-900 tracking-tight">
          {value}
        </h4>
      </div>
    </div>
  </motion.div>
);

const ChartCard = ({ title, subtitle, icon: Icon, children, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    className="bg-white/70 backdrop-blur-xl border border-white/60 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-3xl p-6 hover:shadow-xl transition-all duration-300"
  >
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-4">
        <div className="p-2.5 rounded-xl bg-gradient-to-br from-green-50 to-emerald-100 text-primary-600 shadow-sm">
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900 leading-tight">
            {title}
          </h3>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>
      </div>
    </div>
    <div className="h-72">{children}</div>
  </motion.div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [scanners, setScanners] = useState([]);
  const [tokens, setTokens] = useState(0);

  const [dailyStats, setDailyStats] = useState([]);
  const [daysWindow, setDaysWindow] = useState(7);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [userPhones, setUserPhones] = useState([]);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [todayTotals, setTodayTotals] = useState({ tokens: 0, docs: 0 });

  const hasTokenData = dailyStats && dailyStats.some((d) => d.tokens > 0);
  const hasDocData = dailyStats && dailyStats.some((d) => d.docs > 0);

  // --- Chart Config ---
  const baseChartOptions = {
    chart: {
      toolbar: { show: false },
      // Sin zoom ni selección (31-10-2026): en el móvil, deslizar sobre el gráfico de área arrancaba la
      // selección de ApexCharts y, si el gráfico se había vuelto a pintar, fallaba con
      // «null is not an object (evaluating 'this.gridRect.getBoundingClientRect')» (avisos js_error).
      zoom: { enabled: false },
      selection: { enabled: false },
      fontFamily: "'Inter', sans-serif",
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
      },
      background: "transparent",
      foreColor: "#9ca3af",
    },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 3, lineCap: "round" },
    grid: {
      borderColor: "#f3f4f6",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 0, right: 0, bottom: 0, left: 10 },
    },
    xaxis: {
      categories: dailyStats.map((d) => d.date.slice(5)),
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        style: { colors: "#9ca3af", fontSize: "12px", fontWeight: 500 },
        offsetY: 5,
      },
      tooltip: { enabled: false },
    },
    yaxis: {
      labels: {
        style: { colors: "#9ca3af", fontSize: "12px", fontWeight: 500 },
        offsetX: -10,
      },
    },
    theme: { mode: "light" },
  };

  const areaChartOptions = {
    ...baseChartOptions,
    colors: ["#1aa00d"],
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.2,
        opacityFrom: 0.4,
        opacityTo: 0.05,
        stops: [0, 100],
      },
    },
    stroke: { curve: "smooth", width: 3, colors: ["#1aa00d"] },
    tooltip: {
      theme: "light",
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const date = w.globals.labels[dataPointIndex];
        const value = series[seriesIndex][dataPointIndex];
        return `
          <div class="px-3 py-2 bg-white/90 backdrop-blur border border-green-100 shadow-xl rounded-lg text-sm">
            <div class="font-semibold text-primary-700 mb-0.5">${date}</div>
            <div class="text-gray-600 font-medium">${value} tokens</div>
          </div>
        `;
      },
    },
  };

  const barChartOptions = {
    ...baseChartOptions,
    colors: ["#33c023"],
    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: "50%",
        distributed: false,
      },
    },
    tooltip: {
      theme: "light",
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const date = w.globals.labels[dataPointIndex];
        const value = series[seriesIndex][dataPointIndex];
        return `
          <div class="px-3 py-2 bg-white/90 backdrop-blur border border-green-100 shadow-xl rounded-lg text-sm">
            <div class="font-semibold text-primary-700 mb-0.5">${date}</div>
            <div class="text-gray-600 font-medium">${value} docs</div>
          </div>
        `;
      },
    },
  };

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setLoadingCharts(true);
      try {
        const [scs, tResp, userData] = await Promise.all([
          api.listScanners(),
          api.getTokens(),
          api.getUser().catch(() => ({ phones: [] })),
        ]);

        setScanners(scs);
        setTokens(tResp.tokens_balance || tResp || 0);
        setTrackContext({ credits: Number(tResp.tokens_balance ?? tResp) || 0 });
        const rawPhones = userData?.phones || [];
        let parsedPhonesRaw = rawPhones;
        if (typeof rawPhones === "string") {
          try {
            parsedPhonesRaw = JSON.parse(rawPhones);
          } catch (err) {
            const matched = (rawPhones.match(/\+\d{6,15}/g) || []).map(
              (m) => m
            );
            parsedPhonesRaw = matched.length ? matched : [rawPhones];
          }
        }
        const parsedPhones = (
          Array.isArray(parsedPhonesRaw) ? parsedPhonesRaw : [parsedPhonesRaw]
        )
          .map((p) => {
            if (!p) return null;
            const extracted = extractInternationalNumber(p);
            if (extracted) return extracted; // store as +XXXXXXXX
            return String(p).replace(/[^0-9]/g, "");
          })
          .filter(Boolean);
        setUserPhones(parsedPhones);

        // Usage Summary
        let items = [];
        try {
          const resp = await api.getUsageSummary(daysWindow, sourceFilter);
          items = resp.data?.items || resp.items || resp || [];
        } catch (e) {
          // Fallback logic
          const allHistory = (scs || []).flatMap((s) => s.history || []);
          const normalizePhone = (p) => (p ? String(p).replace(/^\+/, "") : "");

          const filtered =
            sourceFilter === "all"
              ? allHistory
              : allHistory.filter((h) => {
                  const sourceType = (h.sourceType || h.source_type || "").toLowerCase();
                  if (sourceFilter === "manual_upload")
                    return sourceType === "manual_upload" || !sourceType;
                  if (sourceFilter === "whatsapp")
                    return sourceType === "whatsapp";
                  if (sourceFilter === "api")
                    return sourceType === "api";
                  // Phone number filter
                  return (
                    normalizePhone(h.sourceNumber || h.source_number) ===
                    normalizePhone(sourceFilter)
                  );
                });
          items = aggregateHistoryByDay(filtered, daysWindow);
        }

        setDailyStats(items);
        setTodayTotals(getTodayTotals(items));
      } catch (err) {
        console.error("Dashboard load error:", err);
      } finally {
        setLoadingCharts(false);
      }
    };
    load();
  }, [user, daysWindow, sourceFilter]);

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="p-6 max-w-7xl mx-auto space-y-8 pb-20"
    >
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <motion.h2
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-4xl font-extrabold text-gray-900 tracking-tight"
          >
            {t("dashboard.title")}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg text-gray-500 mt-2"
          >
            {t("dashboard.subtitle")}
          </motion.p>
        </div>

        {/* Quick Actions at Top Right */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-wrap gap-3"
        >
          <Link
            to="/upload"
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-full font-semibold shadow-lg shadow-primary-500/30 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
          >
            <FiUpload className="w-5 h-5" />
            <span>{t("dashboard.upload_process")}</span>
          </Link>
          <Link
            to="/scanners"
            className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-full font-semibold hover:bg-gray-50 hover:border-gray-300 transition-all duration-300"
          >
            <FiList className="w-5 h-5" />
            <span>{t("dashboard.manage_scanners")}</span>
          </Link>
        </motion.div>
      </div>

      {/* Stats Grid - Bento Style */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={FiCreditCard}
          label={t("dashboard.tokens_remaining")}
          value={tokens.toLocaleString()}
          colorClass="bg-blue-50 text-blue-600"
          delay={0.1}
        />
        <StatCard
          icon={FiServer}
          label={t("dashboard.scanners")}
          value={scanners.length}
          colorClass="bg-purple-50 text-purple-600"
          delay={0.2}
        />
        <StatCard
          icon={FiZap}
          label={t("dashboard.tokens_used_today")}
          value={todayTotals.tokens.toLocaleString()}
          colorClass="bg-amber-50 text-amber-600"
          delay={0.3}
        />
        <StatCard
          icon={FiFileText}
          label={t("dashboard.docs_today")}
          value={todayTotals.docs.toLocaleString()}
          colorClass="bg-emerald-50 text-emerald-600"
          delay={0.4}
        />
      </div>

      {/* Filters & Controls */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white/60 backdrop-blur-xl border border-white/60 p-4 rounded-2xl shadow-sm flex flex-wrap items-center justify-between gap-4"
      >
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-500">
            <FiFilter className="w-4 h-4" />
            <span>{t("dashboard.source_filter") || "Filter Source"}</span>
          </div>
          <div className="relative group">
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="appearance-none pl-4 pr-10 py-2 rounded-xl bg-white border border-gray-200 text-gray-700 font-medium focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none cursor-pointer hover:border-primary-300 transition-colors"
            >
              <option value="all">
                {t("dashboard.source_all") || "All Sources"}
              </option>
              <option value="whatsapp">
                {t("dashboard.source_whatsapp") || "WhatsApp"}
              </option>
              <option value="manual_upload">
                {t("dashboard.source_manual") || "Manual Upload"}
              </option>
              <option value="api">
                {t("dashboard.source_api") || "API"}
              </option>
              {userPhones.length > 0 && (
                <optgroup label={t("dashboard.source_phones") || "Phone Numbers"}>
                  {userPhones.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-hover:text-primary-500 transition-colors" />
          </div>
        </div>

        <div className="flex bg-gray-100/50 p-1 rounded-xl">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDaysWindow(d)}
              className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-all duration-300 ${
                daysWindow === d
                  ? "bg-white text-primary-700 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </motion.div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <ChartCard
          title={t("dashboard.tokens_spent", { days: daysWindow })}
          subtitle={t("dashboard.daily_usage")}
          icon={FiTrendingUp}
          delay={0.6}
        >
          {loadingCharts ? (
            <div className="w-full h-full flex items-center justify-center">
              <LoadingSkeleton
                variant="chart"
                className="h-full rounded-2xl opacity-80"
              />
            </div>
          ) : hasTokenData ? (
            <Chart
              options={areaChartOptions}
              series={[
                { name: "Tokens", data: dailyStats.map((d) => d.tokens) },
              ]}
              type="area"
              height="100%"
            />
          ) : (
            <EmptyState
              icon={FiTrendingUp}
              text={t("dashboard.no_data")}
              subtext={t("dashboard.no_tokens_yet")}
            />
          )}
        </ChartCard>

        <ChartCard
          title={t("dashboard.documents_processed", { days: daysWindow })}
          subtitle={t("dashboard.processing_activity")}
          icon={FiBarChart2}
          delay={0.7}
        >
          {loadingCharts ? (
            <div className="w-full h-full flex items-center justify-center">
              <LoadingSkeleton
                variant="chart"
                className="h-full rounded-2xl opacity-80"
              />
            </div>
          ) : hasDocData ? (
            <Chart
              options={barChartOptions}
              series={[{ name: "Docs", data: dailyStats.map((d) => d.docs) }]}
              type="bar"
              height="100%"
            />
          ) : (
            <EmptyState
              icon={FiBarChart2}
              text={t("dashboard.no_data")}
              subtext={t("dashboard.no_docs_yet")}
            />
          )}
        </ChartCard>
      </div>
    </motion.main>
  );
}

const EmptyState = ({ icon: Icon, text, subtext }) => (
  <div className="flex flex-col items-center justify-center h-full text-center p-6">
    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
      <Icon className="w-8 h-8 text-gray-300" />
    </div>
    <p className="text-gray-900 font-semibold mb-1">{text}</p>
    <p className="text-sm text-gray-500">{subtext}</p>
  </div>
);
